import React, { useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Save } from "lucide-react";
import { SavedScenario } from "@/hooks/useSavedScenarios";

interface SaveScenarioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string, inputs: SavedScenario['inputs'], results: SavedScenario['results']) => void;
  currentInputs: SavedScenario['inputs'];
  currentResults: SavedScenario['results'];
  language: string;
  formatCurrency: (value: number) => string;
}

const SaveScenarioDialog = ({
  open,
  onOpenChange,
  onSave,
  currentInputs,
  currentResults,
  language,
  formatCurrency
}: SaveScenarioDialogProps) => {
  const [name, setName] = useState('');

  const handleSave = () => {
    const scenarioName = name.trim() ||
      `${language === 'sv' ? 'Scenario' : 'Scenario'} ${new Date().toLocaleDateString()}`;
    onSave(scenarioName, currentInputs, currentResults);
    setName('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Save className="h-5 w-5" />
            {language === 'sv' ? 'Spara scenario' : 'Save Scenario'}
          </DialogTitle>
          <DialogDescription>
            {language === 'sv'
              ? 'Ge ditt scenario ett namn för att spara och jämföra senare'
              : 'Give your scenario a name to save and compare later'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Input
            placeholder={language === 'sv' ? 'T.ex. "5 fastigheter hög volym"' : 'e.g. "5 properties high volume"'}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          />

          {/* Preview of what will be saved */}
          <Card className="p-3 bg-muted/50">
            <p className="text-xs text-muted-foreground mb-2">
              {language === 'sv' ? 'Sparar:' : 'Saving:'}
            </p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span>{currentInputs.callsPerMonth} {language === 'sv' ? 'samtal/mån' : 'calls/mo'}</span>
              <span>{currentInputs.numberOfProperties} {language === 'sv' ? 'fastigheter' : 'properties'}</span>
            </div>
            <motion.p
              className="text-lg font-bold text-brand-600 mt-2"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring" }}
            >
              {formatCurrency(currentResults.totalYearly)}/yr
            </motion.p>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {language === 'sv' ? 'Avbryt' : 'Cancel'}
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            {language === 'sv' ? 'Spara' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveScenarioDialog;
